import { useEffect, useState } from 'react';
import type { AgentLookupKey, LookupOption } from '../../types';
import { ThemedSelect } from '@bizfirst/common-react';
import { useAgentAdminApiClient } from '../../context/AiAgentAdminApiContext';

export interface LookupSelectProps {
  lookupKey: AgentLookupKey;
  value: number | null | undefined;
  onChange: (id: number | null) => void;
  label: string;
  disabled?: boolean;
}

/**
 * Simple remote-backed <select>, one per Agent lookup dropdown (AgentTypeID, AgentCategoryID,
 * LlmProviderID, etc.) — deliberately not flow-studio-core's LookupSelect (workspace-local,
 * carries a global lookupEventBus cache-invalidation system this package doesn't depend on). Uses
 * AgentAdminApiClient.getLookupOptions's simpler per-client cache instead — see that method's
 * comment for the tradeoff.
 */
export function LookupSelect({ lookupKey, value, onChange, label, disabled }: LookupSelectProps) {
  const agentApi = useAgentAdminApiClient();
  const [options, setOptions] = useState<LookupOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    agentApi.getLookupOptions(lookupKey).then((result) => {
      if (cancelled) return;
      setOptions(result.data ?? []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupKey]);

  return (
    <div className="aaa-form-group">
      <label>{label}</label>
      <ThemedSelect
        value={value ?? ''}
        disabled={disabled || loading}
        onChange={(v) => onChange(v ? Number(v) : null)}
        options={[{ value: '', label: loading ? 'Loading…' : '— None —' }, ...options.map((option) => ({ value: option.id, label: option.name }))]}
        ariaLabel={label}
      />
    </div>
  );
}
